import { EyeIcon, IconProps } from '@phosphor-icons/react';
import {
  AccessEnum,
  ServiceAccessEnum,
  ServiceAccessRole,
  WorkspaceStatusEnum,
} from '@sr-sdks/api-sdk-axios';
import { Icon } from '@sr/ui-library';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { StatusBadge } from '../../../../components/Badges/StatusBadge';
import IconWithCircleBackground from '../../../../components/IconWithCircleBackground';
import { AppDataTable } from '../../../../components/data-table';
import { beneficiaryService } from '../../../../reducers/services/beneficiarySlice';
import { BeneficiaryWithServiceAccessRole } from '../../../../reducers/services/beneficiaryState';
import { useAppDispatch, useTypedSelector } from '../../../../stateStore';
import { isLoadingState, toTitleCase } from '../../../../utils/helpers/common';

const renderIcon = (
  icon: React.ForwardRefExoticComponent<IconProps> | any,
  className: string,
  label: string,
) => (
  <>
    <Icon className={className} icon={icon} size="sm" />
    <span className="sr-only">{label}</span>
  </>
);

/**
 * Determines and renders the appropriate icon based on the service access role and permissions
 *
 * @param serviceAccessRole - The service access role object containing access permissions, can be null
 * @param viewAccess - The enum value representing the view permission to check
 * @param editAccess - The enum value representing the edit permission to check
 * @returns JSX element containing the appropriate icon with styling
 */
const DEFAULT_SORT_BY = 'beneficiaryWorkspaceName:asc';

const determineAccessIcon = (
  serviceAccessRole: ServiceAccessRole | null,
  viewAccess: ServiceAccessEnum,
  editAccess: ServiceAccessEnum,
) => {
  if (!serviceAccessRole) {
    return renderIcon('x', 'text-sr-text-brand', 'No access');
  }

  const { access } = serviceAccessRole;
  const allowViewAccess = access[viewAccess] === AccessEnum.Allow;
  const allowEditAccess = access[editAccess] === AccessEnum.Allow;

  if (allowViewAccess && allowEditAccess) {
    return renderIcon('edit', 'text-green-600', 'View, approve and/or upload');
  }

  if (allowViewAccess) {
    return renderIcon(EyeIcon, 'text-sr-text-primary', 'View only');
  }

  return renderIcon('x', 'text-sr-text-brand', 'No access');
};

/**
 * Reusable "Beneficiary Access" table for v3 pages using the AppDataTable component.
 * Preserves exact behavior and data-fetching of the original BeneficiaryAccessTable.
 *
 * Uses Redux selectors for state management (same as the original component).
 */
export const BeneficiaryAccessDataTable = () => {
  const dispatch = useAppDispatch();

  const isBeneficiaryLoading = useTypedSelector(
    beneficiaryService.selectors.isLoading,
  );

  const paginationMeta = useTypedSelector(
    beneficiaryService.selectors.paginationMeta,
  );

  const beneficiaries = useTypedSelector(
    beneficiaryService.selectors.beneficiaries,
  );

  const currentBeneficiary = useTypedSelector(
    beneficiaryService.selectors.currentBeneficiary,
  );

  // Tracks the live sort so it can be passed back into AppDataTable for the
  // header's active/direction state — a hardcoded constant here would freeze
  // AppDataTable's toggle logic on its first direction forever.
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);

  const handlePageRequest = useCallback(
    async (page: number, nextSortBy = DEFAULT_SORT_BY) => {
      if (!currentBeneficiary?.serviceId) return;

      setSortBy(nextSortBy);

      await dispatch(
        beneficiaryService.findAll({
          page,
          pageSize: 50,
          serviceId: currentBeneficiary.serviceId,
          sortBy: nextSortBy,
        }),
      );
    },
    [dispatch, currentBeneficiary],
  );

  // Define columns for the AppDataTable
  const columns: ColumnDef<BeneficiaryWithServiceAccessRole>[] = [
    {
      accessorKey: 'beneficiaryWorkspaceName',
      header: 'Name',
      meta: { sortKey: 'beneficiaryWorkspaceName' },
    },
    {
      accessorKey: 'party',
      cell: ({ row }) =>
        row.original.party ? (
          <IconWithCircleBackground
            color="bg-sr-surface-light-green"
            icon="check"
          />
        ) : (
          <IconWithCircleBackground
            color="bg-sr-surface-light-red"
            icon="x"
            iconColor="text-sr-red"
          />
        ),
      header: 'Party',
    },
    {
      accessorKey: 'beneficiaryType',
      cell: ({ row }) => toTitleCase(row.original.beneficiaryType),
      header: 'Type',
    },
    {
      accessorKey: 'activityStatements',
      cell: ({ row }) =>
        determineAccessIcon(
          row.original.serviceAccessRole,
          ServiceAccessEnum.ViewActivityStatements,
          ServiceAccessEnum.ApproveActivityStatements,
        ),
      enableSorting: false,
      header: 'Activity Statements',
    },
    {
      accessorKey: 'marketingAgreements',
      cell: ({ row }) =>
        determineAccessIcon(
          row.original.serviceAccessRole,
          ServiceAccessEnum.ViewContracts,
          ServiceAccessEnum.UploadContracts,
        ),
      enableSorting: false,
      header: 'Marketing Agreements',
    },
    {
      accessorKey: 'salesReports',
      cell: ({ row }) =>
        determineAccessIcon(
          row.original.serviceAccessRole,
          ServiceAccessEnum.ViewSales,
          ServiceAccessEnum.AddSales,
        ),
      enableSorting: false,
      header: 'Sales Reports',
    },
    {
      accessorKey: 'workspaceStatus',
      cell: ({ row }) => (
        <StatusBadge
          type={
            (row.original.beneficiaryWorkspaceStatus as WorkspaceStatusEnum) ||
            WorkspaceStatusEnum.Disabled
          }
        />
      ),
      enableSorting: false,
      header: 'Workspace Status',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <AppDataTable
        id="beneficiary-access-table"
        columns={columns}
        data={beneficiaries || []}
        emptyState={{
          description:
            'There is currently no beneficiary information to display in this section. Please try again later.',
          title: 'No Records Available',
        }}
        isLoading={isLoadingState(isBeneficiaryLoading)}
        paginationKey="beneficiaryAccessPage"
        paginationMeta={paginationMeta}
        sortBy={sortBy}
        onPageRequest={handlePageRequest}
      />

      {/* Legend showing what each access icon means */}
      <div className="inline-flex flex-wrap items-center justify-start gap-[25px]">
        <div className="flex justify-start gap-2 whitespace-nowrap p-2">
          <div className="relative h-4 w-4 overflow-hidden">
            <Icon className="text-green-600" icon="edit" size="sm" />
          </div>
          <div className="font-['Poppins'] text-sm font-normal text-neutral-600">
            View, approve and upload
          </div>
        </div>
        <div className="flex justify-start gap-2 whitespace-nowrap p-2">
          <div className="relative h-4 w-4 overflow-hidden">
            <Icon className="text-sr-text-primary" icon={EyeIcon} size="sm" />
          </div>
          <div className="font-['Poppins'] text-sm font-normal text-neutral-600">
            View only
          </div>
        </div>
        <div className="flex justify-start gap-2 whitespace-nowrap p-2">
          <div className="relative h-4 w-4">
            <Icon className="text-sr-text-brand" icon="x" size="sm" />
          </div>
          <div className="font-['Poppins'] text-sm font-normal text-neutral-600">
            No access
          </div>
        </div>
      </div>
    </div>
  );
};
